import type { UpgradeHint } from "./pack-upgrade-operation";
export type PendingPackUpgrade = {
	checkoutId: string;
	operationId: string;
	resumeHint: UpgradeHint;
	requestId?: string;
};
const pendingUpgradeKey = (checkoutId: string) => `cart-pack-upgrade:${checkoutId}`;
export function parsePendingUpgrade(raw: string | null, checkoutId: string): PendingPackUpgrade | null {
	if (!raw) return null;
	try {
		const p = JSON.parse(raw) as PendingPackUpgrade;
		const h = p.resumeHint;
		if (
			p.checkoutId !== checkoutId ||
			typeof p.operationId !== "string" ||
			(p.requestId !== undefined && typeof p.requestId !== "string") ||
			!h ||
			!["adding", "removing_source", "cancelling"].includes(h.phase) ||
			typeof h.sourceLineId !== "string" ||
			!Number.isSafeInteger(h.sourceQuantity) ||
			h.sourceQuantity < 1 ||
			(h.replacementLineId !== undefined && typeof h.replacementLineId !== "string")
		)
			return null;
		return {
			checkoutId,
			operationId: p.operationId,
			...(p.requestId ? { requestId: p.requestId } : {}),
			resumeHint: {
				phase: h.phase,
				sourceLineId: h.sourceLineId,
				sourceQuantity: h.sourceQuantity,
				...(h.replacementLineId ? { replacementLineId: h.replacementLineId } : {}),
			},
		};
	} catch {
		return null;
	}
}
function savePendingUpgrade(checkoutId: string, pending: PendingPackUpgrade | null): boolean {
	try {
		if (pending) localStorage.setItem(pendingUpgradeKey(checkoutId), JSON.stringify(pending));
		else localStorage.removeItem(pendingUpgradeKey(checkoutId));
		window.dispatchEvent(new Event("cart-pack-upgrade"));
		return true;
	} catch {
		return false;
	}
}
// All production marker changes share this origin-scoped checkout lock.
// Without Web Locks, recovery writes fail closed; read-only status checks remain available.
async function withPendingLock(checkoutId: string, apply: () => boolean): Promise<boolean> {
	try {
		if (!navigator.locks?.request) return false;
		return await navigator.locks.request(`cart-pack-upgrade:${checkoutId}`, apply);
	} catch {
		return false;
	}
}
export async function replacePendingUpgrade(
	checkoutId: string,
	expected: string | null,
	pending: PendingPackUpgrade | null,
): Promise<boolean> {
	return withPendingLock(checkoutId, () => {
		if (localStorage.getItem(pendingUpgradeKey(checkoutId)) !== expected) {
			window.dispatchEvent(new Event("cart-pack-upgrade"));
			return false;
		}
		return savePendingUpgrade(checkoutId, pending);
	});
}
export function subscribePendingUpgrade(listener: () => void) {
	window.addEventListener("storage", listener);
	window.addEventListener("cart-pack-upgrade", listener);
	return () => {
		window.removeEventListener("storage", listener);
		window.removeEventListener("cart-pack-upgrade", listener);
	};
}
export function readPendingUpgrade(checkoutId: string): string | null {
	try {
		return localStorage.getItem(pendingUpgradeKey(checkoutId));
	} catch {
		return null;
	}
}

const reviewedUpgradeKey = (checkoutId: string) => `cart-pack-review:${checkoutId}`;
export function readReviewedUpgrade(checkoutId: string): string | null {
	try {
		return localStorage.getItem(reviewedUpgradeKey(checkoutId));
	} catch {
		return null;
	}
}
export function parseReviewedUpgrade(
	raw: string | null,
	checkoutId: string,
): import("./pack-upgrade-review").UpgradeReviewAcknowledgement | null {
	try {
		if (!raw) return null;
		const value = JSON.parse(raw) as import("./pack-upgrade-review").UpgradeReviewAcknowledgement;
		if (
			value.checkoutId !== checkoutId ||
			typeof value.operationId !== "string" ||
			!Array.isArray(value.operationIds) ||
			!value.operationIds.every((id) => typeof id === "string") ||
			typeof value.fingerprint !== "string"
		)
			return null;
		return {
			checkoutId,
			operationId: value.operationId,
			operationIds: value.operationIds,
			fingerprint: value.fingerprint,
		};
	} catch {
		return null;
	}
}
export async function saveReviewedUpgrade(
	expected: string | null,
	ack: import("./pack-upgrade-review").UpgradeReviewAcknowledgement,
): Promise<boolean> {
	return withPendingLock(ack.checkoutId, () => {
		if (localStorage.getItem(pendingUpgradeKey(ack.checkoutId)) !== expected) return false;
		try {
			localStorage.setItem(
				reviewedUpgradeKey(ack.checkoutId),
				JSON.stringify({
					checkoutId: ack.checkoutId,
					operationId: ack.operationId,
					operationIds: ack.operationIds,
					fingerprint: ack.fingerprint,
				}),
			);
			window.dispatchEvent(new Event("cart-pack-upgrade"));
			return true;
		} catch {
			return false;
		}
	});
}
